import { useState, useCallback, useEffect } from 'react';

/**
 * useKanbanSelection Hook
 * 
 * Manages card selection state and focus mission events.
 */
const useKanbanSelection = () => {
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  // Sync DOM with React state when state changes (for external updates like focusMission)
  // This ensures the DOM reflects the selection state when it changes via setState
  useEffect(() => {
    // Clear all selected classes first
    document.querySelectorAll('.kanban-card--selected').forEach(el => {
      el.classList.remove('kanban-card--selected');
    });
    // Add selected class to currently selected cards
    selectedTaskIds.forEach(taskId => {
      const cardEl = document.querySelector(`.kanban-card[data-task-id="${taskId}"]`);
      cardEl?.classList.add('kanban-card--selected');
    });
  }, [selectedTaskIds]);

  // Listen for focusMission event from sidebar navigation
  useEffect(() => {
    const handleFocusMission = (e) => {
      const { missionId, view } = e.detail;
      if (view !== 'kanban' || !missionId) return;
      
      // Find the card element and scroll it into view
      setTimeout(() => {
        const cardElement = document.querySelector(`[data-task-id="${missionId}"]`);
        if (cardElement) {
          // Use instant scroll so flash is visible immediately
          cardElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
          
          // Flash highlight after a brief moment for the scroll to complete
          // Duration matches the CSS animation (1.8s pulse + fade)
          setTimeout(() => {
            cardElement.classList.add('kanban-card--highlighted');
            setTimeout(() => {
              cardElement.classList.remove('kanban-card--highlighted');
            }, 2000);
          }, 50);
          
          // Select the task
          setSelectedTaskIds(new Set([missionId]));
        }
      }, 100);
    };
    
    window.addEventListener('focusMission', handleFocusMission);
    return () => window.removeEventListener('focusMission', handleFocusMission);
  }, []);

  // Handle card selection - supports shift+click for multi-select
  // PERFORMANCE: DOM manipulation happens BEFORE state update for instant visual feedback
  const handleTaskClick = useCallback((taskId, isShiftClick) => {
    // Target .kanban-card specifically (not the sortable-card-wrapper which also has data-task-id)
    const cardEl = document.querySelector(`.kanban-card[data-task-id="${taskId}"]`);
    
    if (isShiftClick) {
      // Toggle this card's selection
      const isCurrentlySelected = cardEl?.classList.contains('kanban-card--selected');
      if (isCurrentlySelected) {
        cardEl?.classList.remove('kanban-card--selected');
      } else {
        cardEl?.classList.add('kanban-card--selected');
      }
      // Update React state
      setSelectedTaskIds(prev => {
        const newSet = new Set(prev);
        if (isCurrentlySelected) {
          newSet.delete(taskId);
        } else {
          newSet.add(taskId);
        }
        return newSet;
      });
    } else {
      // Single select: clear all others first, then select this one
      document.querySelectorAll('.kanban-card--selected').forEach(el => {
        el.classList.remove('kanban-card--selected');
      });
      cardEl?.classList.add('kanban-card--selected');
      // Update React state
      setSelectedTaskIds(new Set([taskId]));
    }
  }, []);

  // Deselect when clicking outside cards
  // PERFORMANCE: Immediately update DOM for instant visual feedback (like Flow view)
  const handleBoardClick = useCallback((e) => {
    // Check if click is on board/column background (not on a card)
    const classList = e.target.classList;
    const isBackgroundClick = 
      // Normal kanban mode
      classList.contains('kanban-board') || 
      classList.contains('kanban-column') ||
      classList.contains('kanban-column__cards') ||
      // Swimlane mode
      classList.contains('kanban-swimlanes') ||
      classList.contains('kanban-swimlanes__content') ||
      classList.contains('swimlane-row') ||
      classList.contains('swimlane-row__content') ||
      classList.contains('swimlane-row__column') ||
      classList.contains('swimlane-row__column-wrapper');
    
    if (isBackgroundClick) {
      // Instant DOM feedback: clear all selected classes
      document.querySelectorAll('.kanban-card--selected').forEach(el => {
        el.classList.remove('kanban-card--selected');
      });
      setSelectedTaskIds(new Set());
    }
  }, []);

  return {
    selectedTaskIds,
    setSelectedTaskIds,
    handleTaskClick,
    handleBoardClick,
  };
};

export default useKanbanSelection;

